/**
 * Приглашение нового человека.
 *
 * Два состояния в одном окне: форма и результат. Разделять их на два окна
 * нельзя — ссылка показывается ровно один раз, и закрытое случайным щелчком
 * окно означало бы потерянное приглашение. Поэтому после создания форма
 * сменяется ссылкой, а закрыть окно можно только осознанно.
 *
 * Ссылка равносильна паролю: получить её повторно нельзя, в базе хранится
 * только хеш. Об этом сказано прямо в окне — иначе человек закроет его,
 * полагая, что вернётся позже.
 */

import { CheckIcon, CopyIcon, Loader2Icon, MailPlusIcon, TriangleAlertIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { InvitationDto, MemberRole } from '@project-os/contracts';
import { MEMBER_ROLE_LABELS_RU } from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useInviteMember } from '@/api/members';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDateTime } from '@/lib/format';

const ASSIGNABLE_ROLES: Exclude<MemberRole, 'OWNER'>[] = ['ADMIN', 'MEMBER', 'VIEWER'];

const ROLE_HINTS: Record<Exclude<MemberRole, 'OWNER'>, string> = {
  ADMIN: 'Управление содержимым и участниками',
  MEMBER: 'Создание и изменение проектной информации',
  VIEWER: 'Только чтение',
};

export function InviteMemberDialog({
  open,
  onOpenChange,
  projectId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Exclude<MemberRole, 'OWNER'>>('MEMBER');
  const [result, setResult] = useState<InvitationDto | null>(null);

  const invite = useInviteMember(projectId);

  const reset = () => {
    setEmail('');
    setName('');
    setRole('MEMBER');
    setResult(null);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const submit = async () => {
    if (!email.trim()) return;
    try {
      const invitation = await invite.mutateAsync({
        email: email.trim(),
        name: name.trim() || undefined,
        role,
      });
      setResult(invitation);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось создать приглашение');
    }
  };

  return (
    <Dialog
      open={open}
      // Пока ссылка на экране, окно не закрывается ни щелчком мимо, ни
      // Escape: закрыть его — единственный способ потерять приглашение
      // навсегда, и это должно быть решением, а не промахом.
      onOpenChange={(next) => {
        if (next) return onOpenChange(true);
        if (result) return;
        close();
      }}
    >
      <DialogContent className="sm:max-w-lg" showCloseButton={!result}>
        {result ? (
          <InvitationResult invitation={result} onDone={close} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Пригласить в проект</DialogTitle>
              <DialogDescription>
                Будет заведена учётная запись и создана одноразовая ссылка на установку пароля.
                Письма система не отправляет — ссылку вы передадите человеку сами.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Адрес электронной почты</Label>
                <Input
                  id="invite-email"
                  type="email"
                  autoComplete="off"
                  placeholder="anna@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void submit();
                    }
                  }}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  Он же станет логином. Изменить адрес позже нельзя.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="invite-name">Имя</Label>
                  <Input
                    id="invite-name"
                    placeholder="Необязательно"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Без имени возьмём часть адреса до собаки.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="invite-role">Роль в проекте</Label>
                  <Select
                    value={role}
                    onValueChange={(value) => setRole(value as Exclude<MemberRole, 'OWNER'>)}
                  >
                    <SelectTrigger id="invite-role" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSIGNABLE_ROLES.map((value) => (
                        <SelectItem key={value} value={value}>
                          {MEMBER_ROLE_LABELS_RU[value]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{ROLE_HINTS[role]}</p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close}>
                Отмена
              </Button>
              <Button disabled={!email.trim() || invite.isPending} onClick={() => void submit()}>
                {invite.isPending ? <Loader2Icon className="animate-spin" /> : <MailPlusIcon />}
                Пригласить
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Ссылка на экране: показывается один раз, копируется одной кнопкой. */
function InvitationResult({
  invitation,
  onDone,
}: {
  invitation: InvitationDto;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(invitation.link);
      setCopied(true);
      // Отметка гаснет сама: постоянная галочка перестаёт что-либо значить.
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Буфер обмена недоступен без защищённого соединения. Ссылка остаётся
      // на экране и выделяется вручную — сообщать об этом честнее, чем
      // делать вид, что скопировалось.
      toast.error('Браузер не дал доступ к буферу обмена. Выделите ссылку и скопируйте вручную.');
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {invitation.reissued ? 'Новая ссылка готова' : 'Приглашение создано'}
        </DialogTitle>
        <DialogDescription>
          {invitation.reissued
            ? `У ${invitation.user.email} уже было приглашение — прежняя ссылка погашена, действует только эта.`
            : `Учётная запись ${invitation.user.email} заведена. Осталось передать ссылку.`}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="invite-link">Одноразовая ссылка</Label>
          <div className="flex gap-2">
            <Input
              id="invite-link"
              readOnly
              value={invitation.link}
              className="font-mono text-xs"
              onFocus={(event) => event.currentTarget.select()}
            />
            <Button variant="outline" className="shrink-0" onClick={() => void copy()}>
              {copied ? <CheckIcon className="text-tone-success" /> : <CopyIcon />}
              {copied ? 'Скопировано' : 'Копировать'}
            </Button>
          </div>
        </div>

        <div className="flex gap-2.5 rounded-md border border-tone-warning-border bg-tone-warning-bg p-3">
          <TriangleAlertIcon className="mt-px size-4 shrink-0 text-tone-warning" />
          <div className="space-y-1 text-xs leading-relaxed">
            <p className="font-medium">Ссылка показывается один раз.</p>
            <p className="text-muted-foreground">
              Закроете окно — восстановить её будет нельзя: в системе хранится только отпечаток
              ссылки, а не она сама. Если потеряете, пригласите этого человека ещё раз — выдастся
              новая, а эта перестанет работать.
            </p>
            <p className="text-muted-foreground">
              Действует до {formatDateTime(invitation.expiresAt)} и срабатывает один раз. Передайте
              её лично: ссылка равносильна паролю, и почтового канала в системе нет.
            </p>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button onClick={onDone}>Готово, ссылку передал</Button>
      </DialogFooter>
    </>
  );
}
